// src/ui/screens/settings/AboutScreen.jsx

import React from 'react';
import { useNavigate } from 'react-router-dom';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
  cardLight: '#2C2F36',
};

// ── About Button ────────────────────────────────────────────────────────────
//
// `variant="row"` (default) is the original mobile layout: horizontal,
// icon + title/subtitle + chevron. `variant="card"` is a centered vertical
// layout used by the desktop grid — same icon-path logic, no duplication.

const AboutButton = ({ title, subtitle, icon, iconColor, onTap, variant = 'row' }) => {
  const getIconPath = () => {
    switch (icon) {
      case 'language':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12h9m-9 0a9 9 0 0118 0m-18 0a9 9 0 0118 0m-18 0h1m17 0h1m-1 0a9 9 0 01-18 0m18 0h1m-1 0a9 9 0 01-18 0m18 0h1M3 12h1m17 0h1m-1 0a9 9 0 01-18 0m18 0h1M3 12a9 9 0 0118 0m-18 0a9 9 0 0118 0m-18 0h1m17 0h1m-1 0a9 9 0 01-18 0m18 0h1" />
        );
      case 'link':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        );
      case 'gavel':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12h18M3 12a9 9 0 0118 0M3 12a9 9 0 0118 0m-18 0h1m17 0h1m-1 0a9 9 0 01-18 0m18 0h1M3 12h1m17 0h1m-1 0a9 9 0 01-18 0m18 0h1" />
        );
      default:
        return null;
    }
  };

  if (variant === 'card') {
    return (
      <button
        onClick={onTap}
        className="w-full p-6 rounded-2xl flex flex-col items-center text-center gap-3 border border-white/10 hover:border-white/20 hover:bg-white/[0.03] transition-all"
        style={{ backgroundColor: COLORS.cardLight }}
      >
        <div
          className="w-14 h-14 rounded-xl flex items-center justify-center border"
          style={{
            backgroundColor: `${iconColor}26`,
            borderColor: `${iconColor}4D`,
          }}
        >
          <svg className="w-7 h-7" fill="none" stroke={iconColor} viewBox="0 0 24 24">
            {getIconPath()}
          </svg>
        </div>
        <div>
          <p className="text-white text-base font-bold">{title}</p>
          <p className="text-white/60 text-xs font-medium mt-0.5">{subtitle}</p>
        </div>
      </button>
    );
  }

  return (
    <button
      onClick={onTap}
      className="w-full p-4 rounded-xl flex items-center gap-4 border border-white/10"
      style={{ backgroundColor: COLORS.cardLight }}
    >
      <div
        className="w-[54px] h-[54px] rounded-xl flex items-center justify-center border"
        style={{
          backgroundColor: `${iconColor}26`,
          borderColor: `${iconColor}4D`,
        }}
      >
        <svg className="w-7 h-7" fill="none" stroke={iconColor} viewBox="0 0 24 24">
          {getIconPath()}
        </svg>
      </div>
      <div className="flex-1 text-left">
        <p className="text-white text-lg font-bold">{title}</p>
        <p className="text-white/60 text-xs font-medium">{subtitle}</p>
      </div>
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center"
        style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
      >
        <svg className="w-3.5 h-3.5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </button>
  );
};

// ── AboutScreen ─────────────────────────────────────────────────────────────

export const AboutScreen = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  const handleLaunchWebsite = () => {
    window.open('https://filatom.com', '_blank');
  };

  const handleLaunchLinktree = () => {
    window.open('https://linktr.ee/filatomapp', '_blank');
  };

  const handleNavigateToLegal = () => {
    navigate('/auth/terms-of-service');
  };

  const LegalButton = () => (
    <button
      onClick={handleNavigateToLegal}
      className="w-full py-4 rounded-xl flex items-center justify-center gap-2 border border-filatom-primary/30 hover:bg-white/[0.03] transition-colors"
      style={{ backgroundColor: COLORS.card }}
    >
      <svg className="w-5 h-5 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
      <span className="text-filatom-primary text-sm font-semibold tracking-wide">Legal</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-filatom-bg">
      {/* ══════════════════════════════════════════════════════════════════
          MOBILE LAYOUT (unchanged) — visible below the md breakpoint
         ══════════════════════════════════════════════════════════════════ */}
      <div className="md:hidden">
        {/* Top Bar */}
        <div className="pt-safe-top px-3 pb-1">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="w-10 h-10 rounded-full flex items-center justify-center border border-white/10"
              style={{ backgroundColor: COLORS.card }}
            >
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-white text-xl font-bold">About</span>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 pb-8">
          <div className="mt-2 text-center">
            <p className="text-white text-sm font-bold tracking-wide">
              KNOW MORE ABOUT US
            </p>
          </div>

          <div className="mt-6 p-5 rounded-xl space-y-4" style={{ backgroundColor: COLORS.card }}>
            <AboutButton
              title="Website"
              subtitle="Visit our official website"
              icon="language"
              iconColor="#4285F4"
              onTap={handleLaunchWebsite}
            />

            <AboutButton
              title="Linktree"
              subtitle="All our social media links"
              icon="link"
              iconColor="#39E09B"
              onTap={handleLaunchLinktree}
            />
          </div>

          {/* Legal Button */}
          <div className="mt-4">
            <LegalButton />
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          DESKTOP LAYOUT (new) — visible at md breakpoint and up.
          Sticky header, then the two link options as a centered two-up
          card grid, with the Legal button spanning below.
         ══════════════════════════════════════════════════════════════════ */}
      <div className="hidden md:block">
        <div className="sticky top-0 z-10 backdrop-blur-sm border-b border-white/10" style={{ backgroundColor: `${COLORS.bg}E6` }}>
          <div className="max-w-3xl mx-auto px-8 h-20 flex items-center gap-5">
            <button
              onClick={handleBack}
              className="w-10 h-10 rounded-full flex items-center justify-center border border-white/10 hover:bg-white/10 transition-colors"
              style={{ backgroundColor: COLORS.card }}
              aria-label="Go back"
            >
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-white text-2xl font-bold leading-tight">About</h1>
              <p className="text-white/40 text-sm mt-0.5">Know more about us</p>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-8 py-12">
          <div className="grid grid-cols-2 gap-4">
            <AboutButton
              title="Website"
              subtitle="Visit our official website"
              icon="language"
              iconColor="#4285F4"
              onTap={handleLaunchWebsite}
              variant="card"
            />

            <AboutButton
              title="Linktree"
              subtitle="All our social media links"
              icon="link"
              iconColor="#39E09B"
              onTap={handleLaunchLinktree}
              variant="card"
            />
          </div>

          <div className="mt-4">
            <LegalButton />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutScreen;